
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type PostStatus = 'draft' | 'published' | 'scheduled';

export type BlogPost = {
  id: string;
  title: string;
  excerpt: string | null;
  content: string | null;
  slug: string | null;
  image_url: string | null;
  published: boolean;
  status: PostStatus;
  scheduled_for: string | null;
  published_at: string | null; // Add this field
  metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  user_id: string | null;
};

export type BlogPostsResponse = {
  data: BlogPost[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

export type SortField = "created_at" | "title" | "updated_at" | "scheduled_for";
export type SortOrder = "asc" | "desc";

export type UseBlogPostsParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  status?: PostStatus | 'all';
  onlyPublished?: boolean;
};

export function useBlogPosts({
  page = 1,
  pageSize = 10,
  search = "",
  sortBy = "created_at",
  sortOrder = "desc",
  status = 'all',
  onlyPublished = true,
}: UseBlogPostsParams = {}) {
  return useQuery({
    queryKey: ["blog-posts", page, pageSize, search, sortBy, sortOrder, status, onlyPublished],
    queryFn: async () => {
      try {
        // Calculate range for pagination
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        // Build query
        let countQuery = supabase
          .from("blog_posts")
          .select("id", { count: "exact" });

        let dataQuery = supabase
          .from("blog_posts")
          .select("*")
          .range(from, to);

        // Apply filters
        if (onlyPublished) {
          countQuery = countQuery.eq("published", true);
          dataQuery = dataQuery.eq("published", true);
        } else if (status !== 'all') {
          // Filter by specific status
          countQuery = countQuery.eq("status", status);
          dataQuery = dataQuery.eq("status", status);
        }

        // Apply search if provided
        if (search && search.trim() !== "") {
          const searchTerm = `%${search.trim()}%`;
          const searchFilter = `title.ilike.${searchTerm},excerpt.ilike.${searchTerm},content.ilike.${searchTerm}`;
          
          countQuery = countQuery.or(searchFilter);
          dataQuery = dataQuery.or(searchFilter);
        }

        // Apply sorting
        dataQuery = dataQuery.order(sortBy, { ascending: sortOrder === "asc" });

        // Execute both queries in parallel
        const [countResult, dataResult] = await Promise.all([
          countQuery,
          dataQuery,
        ]);

        // Handle errors
        if (countResult.error) {
          console.error("Error fetching blog post count:", countResult.error);
          throw countResult.error;
        }

        if (dataResult.error) {
          console.error("Error fetching blog posts:", dataResult.error);
          throw dataResult.error;
        }

        // Calculate pagination values
        const total = countResult.count || 0;
        const totalPages = Math.ceil(total / pageSize) || 1; // Ensure at least 1 page

        // Format and return the response
        return {
          data: dataResult.data as BlogPost[],
          pagination: {
            page,
            pageSize,
            total,
            totalPages,
          },
        } as BlogPostsResponse;
      } catch (error) {
        console.error("Error in useBlogPosts:", error);
        toast.error("Failed to load blog posts");
        // Return empty data with pagination info
        return {
          data: [],
          pagination: {
            page,
            pageSize,
            total: 0,
            totalPages: 0,
          },
        } as BlogPostsResponse;
      }
    },
    staleTime: 60000, // 1 minute - reduced from 5 minutes for more frequent updates
    refetchOnWindowFocus: true, // Allow refreshing when window regains focus
  });
}
